import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent, Stack } from "@mui/material";
import { Card, Text } from "@src/appV2/redesign/system/ui-components";
import {
  type ReviewSource,
  type WorkplaceCommentAttributesType,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { parseISO } from "date-fns";

import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { WorkplaceReviewCommentLikesButton } from "./LikesButton";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { ReviewContextPills } from "./ReviewContextPills";
import { VideoCommentPlayer } from "./VideoCommentPlayer";

interface WorkplaceReviewCommentCompactCardProps {
  commentId: string;
  commentText: string;
  commentType?: "WRITTEN" | "VIDEO";
  mediaUrl?: string;
  createdAtIso: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  totalLikes: number;
  workerName: string;
  isLiked: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  workplaceId: string;
  maxLines?: number;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  reviewTag?: WorkplaceCommentAttributesType["reviewTag"];
  shiftWorkerQualification?: string;
  rating?: number;
  useDelayedReviewLayout?: boolean;
  showEngagementActions?: boolean;
  isReadOnly?: boolean;
  source?: ReviewSource;
  reviewUrl?: string;
}

export function WorkplaceReviewCommentCompactCard(props: WorkplaceReviewCommentCompactCardProps) {
  const { onClick, isReadOnly = false, commentType, mediaUrl } = props;
  const hasInlineVideo = commentType === "VIDEO" && isDefined(mediaUrl);

  return (
    <Card outlined variant="tertiary">
      {isReadOnly || hasInlineVideo ? (
        <WorkplaceReviewCommentCompactCardContent {...props} />
      ) : (
        <CardActionArea
          sx={{
            height: "100%",
            minHeight: "11rem",
          }}
          onClick={onClick}
        >
          <WorkplaceReviewCommentCompactCardContent {...props} showEngagementActions={false} />
        </CardActionArea>
      )}
    </Card>
  );
}

interface WorkplaceReviewCommentCompactCardContentProps {
  commentId: string;
  commentText: string;
  commentType?: "WRITTEN" | "VIDEO";
  mediaUrl?: string;
  createdAtIso: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  totalLikes: number;
  workerName: string;
  isLiked: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture?: boolean;
  workplaceId: string;
  maxLines?: number;
  shiftPeriod?: WorkplaceCommentAttributesType["shiftPeriod"];
  patientsPerNurse?: string;
  reviewTag?: WorkplaceCommentAttributesType["reviewTag"];
  shiftWorkerQualification?: string;
  rating?: number;
  useDelayedReviewLayout?: boolean;
  showEngagementActions?: boolean;
  source?: ReviewSource;
  reviewUrl?: string;
}

function WorkplaceReviewCommentCompactCardContent(
  props: WorkplaceReviewCommentCompactCardContentProps
) {
  const {
    commentId,
    commentText,
    commentType,
    mediaUrl,
    createdAtIso,
    onLikeChanged,
    totalLikes,
    workerName,
    isLiked,
    profilePictureUrl,
    isLoadingProfilePicture,
    workplaceId,
    maxLines,
    shiftPeriod,
    patientsPerNurse,
    reviewTag,
    shiftWorkerQualification,
    rating,
    useDelayedReviewLayout,
    showEngagementActions = true,
    source,
    reviewUrl,
  } = props;

  const isVideoComment = commentType === "VIDEO" && isDefined(mediaUrl);

  return (
    <CardContent
      sx={{
        padding: 7,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {useDelayedReviewLayout ? (
        <>
          <Stack spacing={4}>
            <WorkplaceReviewCommentAuthorInfo
              useDelayedReviewLayout
              workerName={workerName}
              profilePictureUrl={profilePictureUrl}
              isLoadingProfilePicture={isLoadingProfilePicture}
              createdAt={parseISO(createdAtIso)}
              shiftWorkerQualification={shiftWorkerQualification}
              reviewUrl={reviewUrl}
            />
            <ReviewContextPills
              shiftPeriod={shiftPeriod}
              patientsPerNurse={patientsPerNurse}
              reviewTag={reviewTag}
              rating={rating}
              source={source}
            />
            {isVideoComment && (
              <VideoCommentPlayer url={mediaUrl} workplaceId={workplaceId} commentId={commentId} />
            )}
            {commentText && (
              <Text
                variant="body2"
                sx={{
                  ...(isDefined(maxLines) && {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: "vertical",
                  }),
                }}
              >
                {commentText}
              </Text>
            )}
          </Stack>

          {showEngagementActions && (
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ marginTop: 4 }}
            >
              <WorkplaceReviewCommentRepliesAndLikesWrapper>
                <WorkplaceReviewCommentLikesButton
                  workplaceId={workplaceId}
                  commentId={commentId}
                  isLiked={isLiked}
                  count={totalLikes}
                  onLikeChanged={onLikeChanged}
                />
              </WorkplaceReviewCommentRepliesAndLikesWrapper>
            </Stack>
          )}
        </>
      ) : (
        <>
          {isVideoComment && (
            <VideoCommentPlayer url={mediaUrl} workplaceId={workplaceId} commentId={commentId} />
          )}
          {commentText && (
            <Text
              variant="body2"
              sx={{
                marginBottom: 6,
                ...(isDefined(maxLines) && {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: "vertical",
                }),
              }}
            >
              {commentText}
            </Text>
          )}

          {showEngagementActions ? (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
              <WorkplaceReviewCommentAuthorInfo
                workerName={workerName}
                profilePictureUrl={profilePictureUrl}
                isLoadingProfilePicture={isLoadingProfilePicture}
                createdAt={parseISO(createdAtIso)}
                reviewUrl={reviewUrl}
              />

              <WorkplaceReviewCommentRepliesAndLikesWrapper>
                <WorkplaceReviewCommentLikesButton
                  workplaceId={workplaceId}
                  commentId={commentId}
                  isLiked={isLiked}
                  count={totalLikes}
                  onLikeChanged={onLikeChanged}
                />
              </WorkplaceReviewCommentRepliesAndLikesWrapper>
            </Stack>
          ) : (
            <WorkplaceReviewCommentAuthorInfo
              workerName={workerName}
              profilePictureUrl={profilePictureUrl}
              isLoadingProfilePicture={isLoadingProfilePicture}
              createdAt={parseISO(createdAtIso)}
              reviewUrl={reviewUrl}
            />
          )}
        </>
      )}
    </CardContent>
  );
}
